
import React from "react";
import { Layout } from "@/components/Layout";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, AlertCircle, Calendar, Clock, Users, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchUserBookings } from "@/lib/supabase";
import { BookingType } from "@/lib/supabase/types";
import { isBookingPast } from "@/components/admin/bookings/utils";
import { format, parseISO } from "date-fns";
import PaymentStatusBadge from "@/components/admin/bookings/PaymentStatusBadge";

const UserBookingsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  
  const { data: bookings, isLoading, error } = useQuery({
    queryKey: ['user-bookings', userId, email],
    queryFn: () => fetchUserBookings(userId || '', email || ''),
  });

  const currentBookings = bookings?.filter(booking => !isBookingPast(booking)) || [];
  const pastBookings = bookings?.filter(booking => isBookingPast(booking)) || [];
  
  // Sort upcoming bookings by date (earliest first)
  const sortedCurrentBookings = [...currentBookings].sort((a, b) => 
    a.classDate && b.classDate 
      ? new Date(a.classDate).getTime() - new Date(b.classDate).getTime() 
      : 0
  );
  
  // Sort past bookings by date (latest first) - CHANGED: reversed the comparison to sort in descending order
  const sortedPastBookings = [...pastBookings].sort((a, b) => 
    a.classDate && b.classDate 
      ? new Date(b.classDate).getTime() - new Date(a.classDate).getTime() 
      : 0
  );

  const userName = bookings && bookings.length > 0 
    ? `${bookings[0].firstName} ${bookings[0].lastName}` 
    : email || 'User';

  const renderBookingTable = (bookingList: BookingType[], isEmpty: string) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Class Name</TableHead>
          <TableHead>Date & Time</TableHead>
          <TableHead>Group Size</TableHead>
          <TableHead>Date Booked</TableHead>
          <TableHead>Payment</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bookingList.length > 0 ? (
          bookingList.map((booking) => (
            <TableRow key={booking.id}>
              <TableCell className="font-medium">{booking.className || 'N/A'}</TableCell>
              <TableCell>
                {booking.classDate ? (
                  <div className="flex flex-col">
                    <span>{format(parseISO(booking.classDate), 'MMM d, yyyy')}</span>
                    <span className="text-muted-foreground text-xs">{booking.classTime}</span>
                  </div>
                ) : (
                  `${booking.classDay} ${booking.classTime}`
                )}
              </TableCell>
              <TableCell>
                {booking.groupSize && booking.groupSize > 1 ? (
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" /> 
                    {booking.groupSize}
                  </div>
                ) : "1"}
              </TableCell>
              <TableCell>
                {booking.createdAt ? format(new Date(booking.createdAt), 'MMM d, yyyy') : 'N/A'}
              </TableCell>
              <TableCell>
                <PaymentStatusBadge booking={booking} />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
              {isEmpty}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <Layout>
      <div className="container py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-3xl font-bold tracking-tight">
              Bookings for {userName}
            </h1>
            <p className="text-muted-foreground">{email}</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
            </Link>
          </Button>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Bookings</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "Failed to load bookings."}
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="text-center py-6">Loading bookings...</div>
        ) : bookings && bookings.length > 0 ? (
          <div className="space-y-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" /> 
                  Upcoming Bookings ({currentBookings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderBookingTable(
                  sortedCurrentBookings, 
                  "No upcoming bookings found."
                )}
              </CardContent>
            </Card>

            <Separator className="my-8" />
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" /> 
                  Booking History ({pastBookings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderBookingTable(
                  sortedPastBookings, 
                  "No booking history found."
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-6">No bookings found for this user.</div>
        )}
      </div>
    </Layout>
  );
};

export default UserBookingsPage;
